import type { PersonName } from "@/lib/person-name"
import { taskPersonLabel } from "@/lib/task-board"
import {
  mentionEntityWorkspaceHref,
  parseTaskMentionToken,
  resolveTaskMentionEntity,
  TASK_MENTION_PENDING,
  TASK_MENTION_START_RE,
  taskMentionDisplay,
  taskMentionEntityKey,
  taskMentionEntityLabel,
  type TaskMentionDirectory,
  type TaskMentionTexts,
} from "@/lib/task-mentions"
import { taskOpenUrl } from "@/lib/task-open-url"
import type { TaskMentionEntityKind } from "@/types/tasks"

const MENTION_TOKEN_RE = new RegExp(TASK_MENTION_START_RE.source, "g")

export type NoteRelatedObject = {
  key: string
  kind: "person" | "task" | TaskMentionEntityKind
  label: string
  subtitle: string | null
  href: string | null
  openLabel: string | null
  person: PersonName | null
  thumbUrl: string | null
  frontView: boolean
}

/** Строки блока «Связанные объекты»: уникальные меншены текущего текста, в порядке появления. */
export function noteRelatedObjects(
  body: string,
  directory: TaskMentionDirectory,
  texts: TaskMentionTexts,
  currentUrl: string,
): NoteRelatedObject[] {
  const rows: NoteRelatedObject[] = []
  const seen = new Set<string>()
  for (const match of body.matchAll(MENTION_TOKEN_RE)) {
    const token = parseTaskMentionToken(match[0])
    if (!token || token.category === "file") continue
    if (token.category === "person") {
      push(rows, seen, personRow(token.id, directory, texts))
      continue
    }
    if (token.category === "task") {
      push(rows, seen, taskRow(token.id, directory, texts, currentUrl))
      continue
    }
    const subId = token.category === "shot" ? null : token.stateId
    push(rows, seen, entityRow(token.category, token.id, subId, directory, texts))
  }
  return rows
}

function push(rows: NoteRelatedObject[], seen: Set<string>, row: NoteRelatedObject) {
  if (seen.has(row.key)) return
  seen.add(row.key)
  rows.push(row)
}

function personRow(id: number, directory: TaskMentionDirectory, texts: TaskMentionTexts): NoteRelatedObject {
  const person = directory.people.get(id)
  const label = person ? taskPersonLabel(person, texts.unnamed) : texts.unnamed
  const email = person?.email?.trim() || null
  return {
    key: `person:${id}`,
    kind: "person",
    label,
    subtitle: email && email !== label ? email : null,
    href: person ? `/company/members/by-user/${id}` : null,
    openLabel: person ? texts.openPerson : null,
    person: person ? { first_name: person.first_name, last_name: person.last_name, email: person.email ?? "" } : null,
    thumbUrl: null,
    frontView: false,
  }
}

function taskRow(
  id: number,
  directory: TaskMentionDirectory,
  texts: TaskMentionTexts,
  currentUrl: string,
): NoteRelatedObject {
  const ref = directory.tasks.get(id)
  const href = ref?.state === "ok" ? taskOpenUrl(currentUrl, ref) : null
  return {
    key: `task:${id}`,
    kind: "task",
    label: taskMentionDisplay(directory, "task", id, "", texts).text,
    subtitle: null,
    href,
    openLabel: href ? texts.openTask : null,
    person: null,
    thumbUrl: null,
    frontView: false,
  }
}

function entityRow(
  kind: TaskMentionEntityKind,
  id: number,
  subId: number | null,
  directory: TaskMentionDirectory,
  texts: TaskMentionTexts,
): NoteRelatedObject {
  const ref = resolveTaskMentionEntity(directory, kind, id, subId)
  const shown = ref && (ref.state === "ok" || ref.state === "sub_deleted") ? ref : null
  const projectId = shown?.state === "ok" ? shown.project_id : undefined
  const href = projectId != null ? mentionEntityWorkspaceHref(projectId, kind, id, subId) : null
  return {
    key: taskMentionEntityKey(kind, id, subId),
    kind,
    label: ref ? taskMentionEntityLabel(ref, texts) : TASK_MENTION_PENDING,
    subtitle: shown?.subtitle?.trim() || null,
    href,
    openLabel: href ? texts.openEntity[kind] : null,
    person: null,
    thumbUrl: shown?.thumb_url ?? null,
    frontView: shown?.front_view === true,
  }
}
